import { useEffect, useRef, useState, useMemo } from "react";
import { CheckCircle2, XCircle, Clock, Ban, Loader2, Radio, Send } from "lucide-react";
import api from "@/lib/api";

const STATUS = {
  sent: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-50", ring: "ring-emerald-100", label: "Terkirim" },
  failed: { icon: XCircle, color: "text-rose-500", bg: "bg-rose-50", ring: "ring-rose-100", label: "Gagal" },
  cooldown: { icon: Clock, color: "text-amber-500", bg: "bg-amber-50", ring: "ring-amber-100", label: "Cooldown" },
  no_email: { icon: Ban, color: "text-slate-400", bg: "bg-slate-50", ring: "ring-slate-100", label: "Tanpa email" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 5) return "baru saja";
  if (s < 60) return `${s} dtk lalu`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} mnt lalu`;
  return `${Math.floor(m / 60)} jam lalu`;
}

export default function BlastActivityFeed({ jobId, leads = [], running, channel = "wa", accent = "sky" }) {
  const [logs, setLogs] = useState([]);
  const pollRef = useRef(null);
  const leadMap = useMemo(() => Object.fromEntries(leads.map((l) => [l.id, l])), [leads]);

  const fetchLogs = async () => {
    try {
      const { data } = await api.get(`/blast/job/${jobId}/logs`);
      setLogs(data.logs || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!jobId) return;
    fetchLogs();
    if (running) {
      pollRef.current = setInterval(fetchLogs, 2000);
      return () => clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, running]);

  const lastSent = logs.find((l) => l.status === "sent");
  const accentText = accent === "violet" ? "text-violet-600" : "text-sky-600";
  const accentBg = accent === "violet" ? "bg-violet-500" : "bg-sky-500";

  return (
    <div className="mt-6" data-testid="blast-activity-feed">
      {/* Now sending banner */}
      {running && (
        <div className="flex items-center gap-3 rounded-xl border border-slate-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3 mb-3">
          <span className="relative flex h-2.5 w-2.5">
            <span className={`absolute inline-flex h-full w-full rounded-full ${accentBg} opacity-60 live-dot`} />
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${accentBg}`} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <Loader2 className={`w-3.5 h-3.5 animate-spin ${accentText}`} />
              {lastSent
                ? <>Baru terkirim ke <span className={accentText}>{leadMap[lastSent.lead_id]?.business_name || lastSent.target_number}</span></>
                : <>Menyiapkan pengiriman…</>}
            </p>
          </div>
          <span className="text-xs font-medium text-slate-400 flex items-center gap-1">
            <Radio className="w-3.5 h-3.5" /> Live
          </span>
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Aktivitas Pengiriman</p>
        <p className="text-xs text-slate-400">{logs.length} aktivitas</p>
      </div>

      <div className="rounded-xl border border-slate-100 divide-y divide-slate-50 max-h-72 overflow-y-auto">
        {logs.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400 flex flex-col items-center gap-2">
            <Send className="w-5 h-5 text-slate-300" />
            Menunggu pengiriman pertama…
          </div>
        ) : (
          logs.map((log, i) => {
            const s = STATUS[log.status] || STATUS.sent;
            const Icon = s.icon;
            const lead = leadMap[log.lead_id] || {};
            const target = channel === "email" ? log.target_number : log.target_number;
            return (
              <div
                key={`${log.lead_id}-${log.sent_at}-${i}`}
                className={`flex items-center gap-3 px-3.5 py-2.5 ${i < 3 ? "blast-row-in" : ""}`}
                data-testid="blast-activity-row"
              >
                <span className={`w-8 h-8 rounded-lg grid place-items-center shrink-0 ring-1 ${s.bg} ${s.ring}`}>
                  <Icon className={`w-4 h-4 ${s.color}`} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">
                    {lead.business_name || (channel === "email" ? "Kontak" : "Nomor")}
                  </p>
                  <p className="text-xs text-slate-400 truncate font-mono">{target || "—"}</p>
                  {log.status === "failed" && log.detail && (
                    <p className="text-[11px] text-rose-400 truncate mt-0.5" title={log.detail}>{log.detail}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-xs font-semibold ${s.color}`}>{s.label}</p>
                  <p className="text-[11px] text-slate-300">{timeAgo(log.sent_at)}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {!running && logs.some((l) => l.status === "failed") && (
        <div className="mt-3 rounded-xl border border-rose-100 bg-rose-50/60 p-4" data-testid="blast-failure-summary">
          <p className="text-sm font-semibold text-rose-700">Sebagian pesan gagal terkirim</p>
          <p className="text-xs text-rose-500 mt-1">
            Penyebab umum: nomor tidak terdaftar di WhatsApp, format nomor salah, atau limit/kuota{channel === "email" ? " email" : " akun Fonnte"} tercapai.
          </p>
          {Array.from(new Set(logs.filter((l) => l.status === "failed" && l.detail).map((l) => l.detail))).slice(0, 4).length > 0 && (
            <ul className="mt-2 space-y-1">
              {Array.from(new Set(logs.filter((l) => l.status === "failed" && l.detail).map((l) => l.detail))).slice(0, 4).map((d, i) => (
                <li key={i} className="text-[11px] text-rose-500 font-mono truncate">• {d}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
